import { Layout } from '@community_dev/pixels'
import { Button, Card, Skeleton, Typography } from 'antd'
import type { JSX } from 'react'
import { useHistory, useParams } from 'react-router'

import { CAPABILITIES } from 'constants/capabilities'
import { ROUTES } from 'constants/routes'
import { useHasCapability } from 'hooks/useCapabilities'
import { useDeleteSSOConfigMutation, useUpdateSSOConfigMutation } from 'hooks/useSSOConfigMutation'
import { useSSOConfigQuery } from 'hooks/useSSOConfigQuery'
import { SSOConfigForm } from 'screens/Clients/Client/components/SSO/SSOConfigForm'
import { SidebarLayout } from 'shared/layouts'

export function SSOConfigEdit(): JSX.Element {
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const { data: ssoConfig, isInitialLoading } = useSSOConfigQuery({ id })
  const { mutateAsync: updateSSOConfig, isLoading: isUpdating, error: updateError } = useUpdateSSOConfigMutation(id)
  const { mutateAsync: deleteSSOConfig, isLoading: isDeleting } = useDeleteSSOConfigMutation(id)
  const canManageSSOConfigs = useHasCapability(CAPABILITIES.ADMIN.SSO_CONFIGS.WRITE)

  const handleDelete = async () => {
    if (!canManageSSOConfigs) return
    await deleteSSOConfig()
    history.push(ROUTES.SSO_CONFIGS)
  }

  return (
    <SidebarLayout>
      <Layout padding="12px">
        <Card
          bordered
          extra={<Button onClick={() => history.push(ROUTES.SSO_CONFIGS)}>Back to list</Button>}
          title={
            <Layout padding="12px 0">
              <Typography.Title level={4} style={{ margin: 0 }}>
                SSO Configs
              </Typography.Title>
              <Typography.Text type="secondary">Edit</Typography.Text>
            </Layout>
          }
        >
          {isInitialLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <SSOConfigForm
              canEdit={canManageSSOConfigs}
              error={updateError}
              initialValues={ssoConfig?.data}
              isDeleting={isDeleting}
              isSaving={isUpdating}
              onDelete={handleDelete}
              onSubmit={(values) => {
                if (!canManageSSOConfigs) return
                updateSSOConfig(values)
              }}
              showDelete
            />
          )}
        </Card>
      </Layout>
    </SidebarLayout>
  )
}
